import type { NotificationPublic, UnreadNotificationsCount } from '@repo/types/notifications';
import type { InfiniteData } from '@tanstack/react-query';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { NotificationKey } from './keys';
import { NotificationService } from './notificationService';

/**
 * Resets all notification counts to zero.
 *
 * @param counts - Current array of unread notification counts
 * @returns Updated counts with all values set to '0'
 */
function resetAllCounts(counts: UnreadNotificationsCount[]): UnreadNotificationsCount[] {
  return counts.map((count) => ({
    ...count,
    count: '0',
  }));
}

/**
 * Marks all notifications in the cache as read.
 *
 * @param data - Current infinite query data containing notification pages
 * @returns Updated data with all notifications marked as read
 */
function markAllNotificationsAsRead(data: InfiniteData<Array<NotificationPublic>>): InfiniteData<Array<NotificationPublic>> {
  return {
    ...data,
    pages: data.pages.map((page) =>
      page.map((notification) => ({
        ...notification,
        read: true,
      }))
    ),
  };
}

/**
 * Hook for marking all notifications as read.
 *
 * Provides optimistic updates to immediately reflect the read state
 * in the UI while the server request is in progress.
 *
 * @returns A mutation object for marking all notifications as read
 *
 * @example
 * ```tsx
 * const { mutate: markAllAsRead, isPending } = usePostReadAllNotifications();
 *
 * const handleMarkAllRead = () => {
 *   markAllAsRead();
 * };
 * ```
 */
export function usePostReadAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => NotificationService.readAllNotifications().then((response) => response.data),

    onMutate: async () => {
      // Cancel any in-flight queries to prevent race conditions
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
          exact: false,
        }),
        queryClient.cancelQueries({
          queryKey: [NotificationKey.UNREAD_NOTIFICATIONS_COUNT],
          exact: false,
        }),
      ]);

      // Optimistically reset all unread counts to zero
      queryClient.setQueriesData<UnreadNotificationsCount[] | undefined>(
        {
          queryKey: [NotificationKey.UNREAD_NOTIFICATIONS_COUNT],
          exact: false,
        },
        (currentCounts) => {
          if (!currentCounts) {
            return undefined;
          }
          return resetAllCounts(currentCounts);
        }
      );

      // Optimistically mark all cached notifications as read for every filter variant
      queryClient.setQueriesData<InfiniteData<Array<NotificationPublic>> | undefined>(
        {
          queryKey: [NotificationKey.NOTIFICATIONS_INFINITE],
          exact: false,
        },
        (currentData) => {
          if (!currentData) {
            return undefined;
          }
          return markAllNotificationsAsRead(currentData);
        }
      );
    },
  });
}
